import { Box, Button, Grid2, Typography } from "@mui/material";
import axios from "axios";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

const Step1 = ({
  sra,
  setSra,
  setStep,
  storedUser,
  fileNames,
  setFileNames,
}) => {
  const navigate = useNavigate();
  const [errors, setErrors] = useState({});
  const [btnLoading, setBtnLoading] = useState(false);
  const orgId = JSON.parse(localStorage.getItem("org"));
  const [vendors, setVendors] = useState([]);
  const [amountLoading, setAmountLoading] = useState(false);
  const [amountDetails, setAmountDetails] = useState(null);
  const [amountError, setAmountError] = useState("");
  const [errorOpen, setErrorOpen] = useState(false);
  const [errorMsg, setErrorMsg] = useState();

  useEffect(() => {
    const getVendors = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/vendor-list/org?orgId=${orgId}`,
          {
            headers: {
              Authorization: `Bearer ${storedUser.accessToken}`,
            },
          },
        );

        setVendors(res.data);
      } catch (error) {
        console.log(error.response?.data?.Exception || "Access denied");
      }
    };

    getVendors();
  }, []);

  const handleVendorChange = (e) => {
    const selectedCode = e.target.value;

    const selectedVendor = vendors.find(
      (vendor) => vendor.code === selectedCode,
    );

    if (selectedVendor) {
      setSra((prev) => ({
        ...prev,
        vendorCode: selectedVendor.code || "",
        vendorName: selectedVendor.name || "",
      }));
    } else {
      setSra((prev) => ({
        ...prev,
        vendorCode: "",
        vendorName: "",
      }));
    }

    setErrors((prev) => ({
      ...prev,
      code: "",
      name: "",
      details: "",
    }));
  };

  const handleChange = (e) => {
    setSra({ ...sra, [e.target.name]: e.target.value });
    setErrors({ ...errors, [e.target.name]: "" });
  };

  const validateForm = () => {
    const newErrors = {};
    const data = sra || {};

    const isEmpty = (value) => !value || value.trim?.() === "";

    if (isEmpty(data.title)) {
      newErrors.title = "Title is required";
    }

    if (isEmpty(data.number)) {
      newErrors.number = "Invoice number is required";
    }

    if (!data.date) {
      newErrors.date = "Invoice Date is required";
    } else {
      const selectedDate = new Date(data.date);
      const today = new Date();
      selectedDate.setHours(0, 0, 0, 0);
      today.setHours(0, 0, 0, 0);

      if (selectedDate > today) {
        newErrors.date = "Invoice Date cannot be a future date";
      }
    }

    if (!data.dueDate) {
      newErrors.dueDate = "Due Date of Payment is required";
    } else {
      const selectedDate = new Date(data.date);
      const today = new Date();
      selectedDate.setHours(0, 0, 0, 0);
      today.setHours(0, 0, 0, 0);

      if (selectedDate > today) {
        newErrors.dueDate = "Due Date of Payment cannot be a future date";
      }
    }

    if (isEmpty(data.basicAmount)) {
      newErrors.basicAmount = "Basic Value is required";
    } else if (isNaN(data.basicAmount) || Number(data.basicAmount) <= 0) {
      newErrors.basicAmount = "Enter a valid amount";
    }

    if (isEmpty(data.amount)) {
      newErrors.amount = "Amount/Breakdown is required";
    } else if (isNaN(data.amount) || Number(data.amount) <= 0) {
      newErrors.amount = "Enter a valid amount";
    }

    if (isEmpty(data.vendorName)) {
      newErrors.vendorName = "Vendor Name is required";
    }

    if (isEmpty(data.vendorCode)) {
      newErrors.vendorCode = "Vendor code is required";
    }

    // if (isEmpty(data.po)) {
    //   newErrors.po = "PO/Service Order Number is required";
    // }

    if (isEmpty(data.dept)) {
      newErrors.dept = "Department is required";
    }

    if (isEmpty(data.gl)) {
      newErrors.gl = "GL is required";
    }

    if (isEmpty(data.costCenter)) {
      newErrors.costCenter = "Cost center is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleUpdate = async () => {
    if (!validateForm()) return;
    setBtnLoading(true);
    try {
      setStep((prev) => prev + 1);
    } catch (error) {
      console.log(error);
    } finally {
      setBtnLoading(false);
    }
  };

  const handleBack = () => {
    navigate("/approval/dashboard");
  };

  const poRegex = /^NHPL-\d{4}-\d{2}-SO-\d{3}$/;

  const handleCalculateAmount = async () => {
    const po = sra?.po?.trim();

    // PO is optional
    if (!po || po === "") {
      setAmountDetails(null);
      setAmountError("");
      return;
    }

    // PO entered, so validate format
    if (!poRegex.test(po)) {
      setAmountDetails(null);
      setAmountError("Enter a valid PO/Service Order Number");
      return;
    }

    setAmountLoading(true);
    setAmountError("");
    setAmountDetails(null);

    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/draft-invoice-approval/get-amounts?soId=${sra.po}`,
        {
          headers: {
            Authorization: `Bearer ${storedUser.accessToken}`,
          },
        },
      );

      setAmountDetails(res.data);
    } catch (error) {
      console.log(error);

      setAmountError(
        error.response?.data?.Exception ||
          "Unable to calculate the invoice amount",
      );
    } finally {
      setAmountLoading(false);
    }
  };

  useEffect(() => {
    handleCalculateAmount();
  }, [sra?.po]);

  return (
    <div style={{ position: "relative" }}>
      {btnLoading && <div className="loading-div"></div>}
      <h2>Invoice Approval Form</h2>
      <div className="progress-container">
        <span className="blue-bar" style={{ width: "20%" }}></span>
      </div>
      <div className="dashboard-container">
        <Typography className="form-title">Details</Typography>

        <Grid2 container columnSpacing={2}>
          <Grid2 size={{ xs: 12, sm: 4 }}>
            <label htmlFor="title">
              Title <span style={{ color: "#FF0000" }}>*</span>
            </label>
            <input
              className={`${errors.title && "input-error"}`}
              type="text"
              name="title"
              id="title"
              onChange={handleChange}
              value={sra?.title || ""}
            />
            {errors.title && <p className="error">{errors.title}</p>}
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 4 }}>
            <label htmlFor="sapNumber">SAP Document Number</label>
            <input
              className={`${errors.sapNumber && "input-error"}`}
              type="text"
              name="sapNumber"
              id="sapNumber"
              onChange={handleChange}
              value={sra?.sapNumber || ""}
            />
            {errors.sapNumber && <p className="error">{errors.sapNumber}</p>}
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 4 }}>
            <label htmlFor="number">
              Invoice Number <span style={{ color: "#FF0000" }}>*</span>
            </label>
            <input
              className={`${errors.number && "input-error"}`}
              type="text"
              name="number"
              id="number"
              onChange={handleChange}
              value={sra?.number || ""}
            />
            {errors.number && <p className="error">{errors.number}</p>}
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 4 }}>
            <label htmlFor="date">
              Invoice Date <span style={{ color: "#FF0000" }}>*</span>
            </label>
            <input
              className={`${errors.date && "input-error"}`}
              type="date"
              name="date"
              id="date"
              onChange={handleChange}
              value={
                sra?.date ? new Date(sra.date).toISOString().split("T")[0] : ""
              }
            />
            {errors.date && <p className="error">{errors.date}</p>}
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 4 }}>
            <label htmlFor="dueDate">
              Due Date of Payment
              <span style={{ color: "#FF0000" }}>*</span>
            </label>
            <input
              className={`${errors.dueDate && "input-error"}`}
              type="date"
              name="dueDate"
              id="dueDate"
              onChange={handleChange}
              value={
                sra?.dueDate
                  ? new Date(sra.dueDate).toISOString().split("T")[0]
                  : ""
              }
            />
            {errors.dueDate && <p className="error">{errors.dueDate}</p>}
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 4 }}>
            <label htmlFor="basicAmount">
              Basic Value
              <span style={{ color: "#FF0000" }}>*</span>
            </label>
            <input
              className={`${errors.basicAmount && "input-error"}`}
              type="number"
              step="0.01"
              name="basicAmount"
              id="basicAmount"
              onChange={handleChange}
              value={sra?.basicAmount || ""}
            />
            {errors.basicAmount && (
              <p className="error">{errors.basicAmount}</p>
            )}
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 4 }}>
            <label htmlFor="taxAmount">
              Tax Value
              <span style={{ color: "#FF0000" }}>*</span>
            </label>
            <input
              className={`${errors.taxAmount && "input-error"}`}
              type="number"
              step="0.01"
              name="taxAmount"
              id="taxAmount"
              onChange={handleChange}
              value={sra?.taxAmount || ""}
            />
            {errors.taxAmount && <p className="error">{errors.taxAmount}</p>}
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 4 }}>
            <label htmlFor="taxValue">Taxable Value</label>
            <input
              className={`${errors.taxValue && "input-error"}`}
              type="number"
              step="0.01"
              name="taxValue"
              id="taxValue"
              onChange={handleChange}
              value={sra?.taxValue || ""}
            />
            {errors.taxValue && <p className="error">{errors.taxValue}</p>}
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 4 }}>
            <label htmlFor="amount">
              Invoice Value Including GST{" "}
              <span style={{ color: "#FF0000" }}>*</span>
            </label>
            <input
              className={`${errors.amount && "input-error"}`}
              type="number"
              step="0.01"
              name="amount"
              id="amount"
              onChange={handleChange}
              value={
                sra?.basicAmount !== undefined && sra?.taxAmount !== undefined
                  ? Number(sra.basicAmount || 0) + Number(sra.taxAmount || 0)
                  : ""
              }
            />
            {errors.amount && <p className="error">{errors.amount}</p>}
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 4 }}>
            <label htmlFor="vendorCode">
              Vendor Code <span style={{ color: "#FF0000" }}>*</span>
            </label>
            <select
              name="vendorCode"
              id="vendorCode"
              onChange={handleVendorChange}
              value={sra?.vendorCode || ""}
              className={`${errors.vendorCode && "input-error"}`}
            >
              <option value="">---Select---</option>
              {vendors?.map((vendor) => (
                <option key={vendor.code} value={vendor.code}>
                  {vendor.code}
                </option>
              ))}
            </select>
            {errors.vendorCode && <p className="error">{errors.vendorCode}</p>}
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 4 }}>
            <label htmlFor="vendorName">
              Vendor Name <span style={{ color: "#FF0000" }}>*</span>
            </label>
            <input
              className={`${errors.vendorName && "input-error"}`}
              type="text"
              name="vendorName"
              id="vendorName"
              onChange={handleChange}
              value={sra?.vendorName || ""}
            />
            {errors.vendorName && <p className="error">{errors.vendorName}</p>}
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 4 }}>
            <label htmlFor="po">
              PO/Service Order Number{" "}
              {/* <span style={{ color: "#FF0000" }}>*</span> */}
            </label>

            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                alignItems: "end",
                gap: 1,
              }}
            >
              <input
                className={`${errors.po && "input-error"}`}
                type="text"
                name="po"
                id="po"
                onChange={(e) => {
                  handleChange(e);
                  setAmountDetails(null);
                  setAmountError("");
                }}
                value={sra?.po || ""}
                style={{
                  flex: 1,
                  marginBottom: "0",
                }}
              />

              <Button
                variant="outlined"
                onClick={handleCalculateAmount}
                disabled={amountLoading || !sra?.po?.trim()}
                sx={{
                  height: "48px !important",
                  borderRadius: "8px",
                  textTransform: "none",
                  fontWeight: 600,
                  marginBottom: "0 !important",
                }}
              >
                {amountLoading ? "Calculating..." : "Calculate"}
              </Button>
            </Box>

            {errors.po && <p className="error">{errors.po}</p>}

            {amountError && (
              <Box
                sx={{
                  mt: 1,
                  p: 1.2,
                  borderRadius: "8px",
                  backgroundColor: "#fff3f3",
                  border: "1px solid #ffcdd2",
                  color: "#d32f2f",
                  fontSize: "12px",
                }}
              >
                {amountError}
              </Box>
            )}

            {amountDetails && (
              <Box
                sx={{
                  mt: 0.8,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  flexWrap: "wrap",
                  fontSize: "11px",
                }}
              >
                <Typography
                  component="span"
                  sx={{
                    fontSize: "11px",
                    color: "#666",
                  }}
                >
                  SO:
                </Typography>

                <Typography
                  component="span"
                  sx={{
                    fontSize: "11px",
                    fontWeight: 600,
                  }}
                >
                  ₹{Number(amountDetails.soAmount || 0).toLocaleString("en-IN")}
                </Typography>

                <Typography
                  component="span"
                  sx={{
                    fontSize: "11px",
                    color: "#aaa",
                  }}
                >
                  •
                </Typography>

                <Typography
                  component="span"
                  sx={{
                    fontSize: "11px",
                    color: "#666",
                  }}
                >
                  Invoiced:
                </Typography>

                <Typography
                  component="span"
                  sx={{
                    fontSize: "11px",
                    fontWeight: 600,
                  }}
                >
                  ₹
                  {Number(amountDetails.totalInvoiceAmount || 0).toLocaleString(
                    "en-IN",
                  )}
                </Typography>

                <Typography
                  component="span"
                  sx={{
                    fontSize: "11px",
                    color: "#aaa",
                  }}
                >
                  •
                </Typography>

                <Typography
                  component="span"
                  sx={{
                    fontSize: "11px",
                    fontWeight: 700,
                    color:
                      Number(amountDetails.remainingAmount || 0) < 0
                        ? "#d32f2f"
                        : "#1976d2",
                  }}
                >
                  Remaining: ₹
                  {Number(amountDetails.remainingAmount || 0).toLocaleString(
                    "en-IN",
                  )}
                </Typography>
              </Box>
            )}
          </Grid2>

          {/* Department */}
          <Grid2 size={{ xs: 12, sm: 4 }}>
            <label htmlFor="dept">
              Requesting Department <span style={{ color: "#FF0000" }}>*</span>
            </label>
            <input
              className={`${errors.dept && "input-error"}`}
              type="text"
              name="dept"
              id="dept"
              onChange={handleChange}
              value={sra?.dept || ""}
            />
            {errors.dept && <p className="error">{errors.dept}</p>}
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 4 }}>
            <label htmlFor="gl">
              GL <span style={{ color: "#FF0000" }}>*</span>
            </label>
            <input
              className={`${errors.gl && "input-error"}`}
              type="text"
              name="gl"
              id="gl"
              onChange={handleChange}
              value={sra?.gl || ""}
            />
            {errors.gl && <p className="error">{errors.gl}</p>}
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 4 }}>
            <label htmlFor="costCenter">
              Cost Center <span style={{ color: "#FF0000" }}>*</span>
            </label>
            <input
              className={`${errors.costCenter && "input-error"}`}
              type="text"
              name="costCenter"
              id="costCenter"
              onChange={handleChange}
              value={sra?.costCenter || ""}
            />
            {errors.costCenter && <p className="error">{errors.costCenter}</p>}
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 4 }}>
            <label htmlFor="remark">Remark</label>
            <input
              className={`${errors.remark && "input-error"}`}
              type="text"
              name="remark"
              id="remark"
              onChange={handleChange}
              value={sra?.remark || ""}
            />
            {errors.remark && <p className="error">{errors.remark}</p>}
          </Grid2>
        </Grid2>
      </div>

      <Box className="btn-container" sx={{ gap: 3 }}>
        <Button className="btn-outlined" onClick={handleBack}>
          Back
        </Button>
        <Button
          className="btn-contained"
          onClick={handleUpdate}
          disabled={btnLoading}
        >
          {btnLoading ? "Submitting..." : "Next"}
        </Button>
      </Box>
    </div>
  );
};

export default Step1;
